// Typed breakdown operations — the deterministic half of the split surface.
//
// Every adjustment a user can make to a proposed split used to leave the
// browser as a natural-language sentence ("Breakdown: remove sub-issue 3; fold
// its scope into the others."), reach the sandbox with no server state
// attached, and come back as a WHOLE new partition. Rename, remove, merge,
// move-criterion, add and park are total functions over `(children, parked)` —
// so none of them needs a model, and none of them may re-author a piece the
// user did not touch.
//
// THE ASYMMETRY IS THE INVARIANT. `modelOpSchema` has no member that retires a
// criterion; `clientOpSchema` does. A model facing a `coverage_gap` deny
// therefore cannot escape it by dropping the criterion — its only legal moves
// are to re-home it or to ask the user. Scope reduction is exclusively a typed
// USER act, which is precisely the decision the old free-text lane made on the
// user's behalf.
//
// PURE: no clock, no store, no I/O, no model, no `node:crypto` (this module
// ships to the browser through the readiness barrel, same as
// `splitAcceptanceCriteria` already does). The SERVER runs it, inside the
// store's per-key critical section. It is written to be browser-safe so an
// optimistic client render can share it, but nothing in `apps/web` calls it
// today — the card sends a typed op and renders what comes back.
//
// No regex lookbehind anywhere in here — Safari 14 ships this module.

import { z } from "zod";

import {
  CHILD_APPROACH_WIRE_MAX,
  CHILD_SCOPE_WIRE_MAX,
  CHILD_TITLE_WIRE_MAX,
  computeScopeSig,
  criterionIdToIndex,
  criterionIdsFor,
  isServerLocalId,
  type BreakdownChild,
  type ParkedCriterion,
} from "./breakdown.js";

// ---------------------------------------------------------------------------
// Op vocabulary
// ---------------------------------------------------------------------------

const childRef = z.string().min(1).max(24);
const criterionRef = z.string().min(1).max(10); // "AC<n>" — one id plane

/** Where a removed child's criteria go, per criterion. */
export const dispositionSchema = z.object({
  criterionId: criterionRef,
  to: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("child"), childId: childRef }),
    z.object({ kind: z.literal("park") }),
  ]),
});
export type Disposition = z.infer<typeof dispositionSchema>;

// The op MEMBERS, named so the two doors can be composed out of them rather
// than one being declared a superset of the other. Plain `z.object`s inside a
// `z.discriminatedUnion` — no `.refine()`, so the shape stays `.omit()`-able at
// the MCP and servant seams and every cross-field rule lands in the reducer,
// where the deny can carry repair guidance instead of a bare
// `input_validation_failed`.

const renameOp = z.object({
  op: z.literal("rename"),
  childId: childRef,
  title: z.string().min(1).max(CHILD_TITLE_WIRE_MAX),
});
const editScopeOp = z.object({
  op: z.literal("edit_scope"),
  childId: childRef,
  description: z.string().min(1).max(CHILD_SCOPE_WIRE_MAX),
  /** Re-authored build note. OMITTING it CLEARS the stored one: a re-worded
   *  scope carrying the old "how to build it" is exactly the lie this design
   *  exists to prevent, and a model that means to keep it can re-send it. */
  approach: z.string().max(CHILD_APPROACH_WIRE_MAX).optional(),
});
const mergeOp = z.object({
  op: z.literal("merge"),
  fromChildId: childRef,
  intoChildId: childRef,
});
const moveCriteriaOp = z.object({
  op: z.literal("move_criteria"),
  criterionIds: z.array(criterionRef).min(1).max(30),
  toChildId: childRef,
});
/** Remove a piece, re-homing every criterion it uniquely covers onto a
 *  SURVIVING piece. The model's only removal verb — it cannot park. */
const removeRehomeOp = z.object({
  op: z.literal("remove_rehome"),
  childId: childRef,
  rehome: z
    .array(z.object({ criterionId: criterionRef, toChildId: childRef }))
    .max(30),
});
const addChildOp = z.object({
  op: z.literal("add_child"),
  title: z.string().min(1).max(CHILD_TITLE_WIRE_MAX),
  description: z.string().min(1).max(CHILD_SCOPE_WIRE_MAX),
  criterionIds: z.array(criterionRef).min(1).max(30),
  approach: z.string().max(CHILD_APPROACH_WIRE_MAX).optional(),
});
const removeOp = z.object({
  op: z.literal("remove"),
  childId: childRef,
  disposition: z.array(dispositionSchema).max(30),
});
const parkCriteriaOp = z.object({
  op: z.literal("park_criteria"),
  criterionIds: z.array(criterionRef).min(1).max(30),
});
const unparkCriteriaOp = z.object({
  op: z.literal("unpark_criteria"),
  parkedTexts: z.array(z.string().min(1)).min(1).max(30),
  toChildId: childRef,
});
/** Routed to the model with ONE released slot — it needs genuinely new prose. */
const splitFurtherOp = z.object({ op: z.literal("split_further"), childId: childRef });
const rewriteScopeOp = z.object({ op: z.literal("rewrite_scope"), childId: childRef });
/** Routed to the model for interpretation only — no released slots. */
const freeTextOp = z.object({
  op: z.literal("free_text"),
  instruction: z.string().min(1).max(2000),
});

/**
 * Ops a MODEL may name. Note what is absent: nothing here can retire a
 * criterion, and nothing here can restate the whole array.
 */
export const modelOpSchema = z.discriminatedUnion("op", [
  renameOp,
  editScopeOp,
  mergeOp,
  moveCriteriaOp,
  removeRehomeOp,
  addChildOp,
]);
export type ModelBreakdownOp = z.infer<typeof modelOpSchema>;

/**
 * Ops the CLIENT may send. NOT a superset of the model's — the two doors
 * overlap, and each one has a member the other must not have.
 *
 * Client-only: `remove` / `park_criteria` / `unpark_criteria`, because scope
 * reduction is a typed USER act (the asymmetry at the top of this file), plus
 * the three model-routed ops that only a user can ask for.
 *
 * MODEL-ONLY: `edit_scope`. A scope line is the piece's half of the split's
 * prose, and the split-level `rationale` is the other half — `rationaleSig`
 * covers the partition SHAPE (piece count, titles, which criteria sit where)
 * and deliberately not the descriptions, on the grounds that the one op which
 * rewrites a description arrives through `propose_breakdown_ops`, which
 * re-authors the rationale in the same call. That was a comment, not a rule:
 * `edit_scope` sat in the client union too, `handleReadinessBreakdownOp` does
 * not re-stamp `rationaleSig`, and one client frame would therefore have
 * rewritten a piece's scope line with zero model turns while the rationale
 * describing it stayed published — the exact staleness `rationaleSig` exists
 * to stop (live #1081 shipped three sub-issues each saying the work "was split
 * into four pieces"). It held only because no UI sent the op. The client's way
 * to re-word a scope line is `rewrite_scope`, which routes to the model with
 * that one child released and comes back with prose that matches.
 */
export const clientOpSchema = z.discriminatedUnion("op", [
  renameOp,
  mergeOp,
  moveCriteriaOp,
  removeRehomeOp,
  addChildOp,
  removeOp,
  parkCriteriaOp,
  unparkCriteriaOp,
  splitFurtherOp,
  rewriteScopeOp,
  freeTextOp,
]);
export type ClientBreakdownOp = z.infer<typeof clientOpSchema>;

/** Every op the reducer can execute, whichever door it came through. The two
 *  unions overlap without either containing the other, so neither one alone is
 *  the reducer's input type. */
export type BreakdownOp = ModelBreakdownOp | ClientBreakdownOp;

/** Ops the reducer executes itself. The three below route to a model turn and
 *  never reach `applyBreakdownOps`. */
export const MODEL_ROUTED_OPS = ["split_further", "rewrite_scope", "free_text"] as const;
export const isModelRouted = (
  op: BreakdownOp,
): op is Extract<BreakdownOp, { op: (typeof MODEL_ROUTED_OPS)[number] }> =>
  (MODEL_ROUTED_OPS as ReadonlyArray<string>).includes(op.op);

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export interface BreakdownOpsOk {
  readonly ok: true;
  readonly children: ReadonlyArray<BreakdownChild>;
  /** childIds an op NAMED. Everything else must be byte-identical — see
   *  `assertUnchangedExcept`, which is the executable form of that promise. */
  readonly touched: ReadonlySet<string>;
  /** Criteria leaving the ticket, as `{index into the PRE-op criteria, text}`. */
  readonly parking: ReadonlyArray<{ readonly index: number; readonly text: string }>;
  /** Parked rows coming back, in the order they should re-enter the AC list. */
  readonly unparking: ReadonlyArray<ParkedCriterion>;
  /**
   * The acceptance-criteria list AFTER these ops — the array `coveredCriteria`
   * indexes into.
   *
   * The caller used to rebuild this itself (`survivors ++ unparked`), which meant
   * two implementations of the ordering had to agree and the reducer's was the
   * only one that could see the remap. They stopped agreeing the moment un-park
   * started restoring a row to its ORIGINAL position: a caller-side append puts
   * the text at a different index than the child's `coveredCriteria` points at,
   * i.e. each piece rendered a different criterion's row. Returning it makes the
   * two identical by construction rather than by review.
   */
  readonly criteria: ReadonlyArray<string>;
  readonly nextChildSeq: number;
}
export interface BreakdownOpsErr {
  readonly ok: false;
  readonly reason: string;
}
export type BreakdownOpsResult = BreakdownOpsOk | BreakdownOpsErr;

export interface ApplyBreakdownOpsParams {
  readonly children: ReadonlyArray<BreakdownChild>;
  /** Server split of the CURRENT acceptance-criteria green. */
  readonly criteria: ReadonlyArray<string>;
  readonly parked: ReadonlyArray<ParkedCriterion>;
  readonly maxChildren: number;
  readonly ops: ReadonlyArray<BreakdownOp>;
  /** Injected so this module stays crypto-free and the browser can render
   *  optimistically with a placeholder minter. */
  readonly mintChildId: (seq: number) => string;
  readonly nextChildSeq: number;
  /** False once the parent card has been applied — the description is already
   *  on GitLab and there is no post-apply acceptance-criteria write lane. */
  readonly criteriaEditable: boolean;
}

const MIN_CHILDREN = 2;

/** Mutable working copy of a child while ops are applied. */
interface Working {
  readonly childId: string;
  base: BreakdownChild;
  title: string;
  description: string;
  covered: Set<number>;
  touched: boolean;
  /** The build note as it stands after the ops applied so far. */
  approach: string | undefined;
  /**
   * The scope signature `approach` was authored AGAINST — `base.scopeSig` for a
   * carried-forward note, or a signature computed at the op that wrote a new
   * one. The output compares it to the RECOMPUTED signature, which is what
   * makes "a build note may not outlive its scope" a postcondition instead of N
   * per-handler clears the next op author can forget.
   *
   * A separate field rather than `base.scopeSig` because `add_child`'s
   * placeholder base has no signature at all: comparing against it would clear
   * the note the very op that authored it just wrote.
   */
  approachSig: string | undefined;
}

const err = (reason: string): BreakdownOpsErr => ({ ok: false, reason });

const namedCriterion = (
  criteria: ReadonlyArray<string>,
  index: number,
): string => `AC${String(index + 1)} "${criteria[index] ?? ""}"`;

/**
 * Apply a list of client ops to a partition.
 *
 * Criterion indices are remapped EXACTLY ONCE, at the end, after all parking is
 * known — remapping per-op would make an op list's meaning depend on its order
 * in a way no caller could reason about.
 */
export const applyBreakdownOps = (
  params: ApplyBreakdownOpsParams,
): BreakdownOpsResult => {
  const { criteria, maxChildren } = params;

  // Every child must be addressable before any op runs. A partition with an
  // un-backfilled row is a caller bug (`ensureChildIds` runs on read), not a
  // user-repairable deny.
  const working: Working[] = [];
  for (const c of params.children) {
    if (c.childId === undefined) return err("child_not_addressable");
    // The persisted indices must still resolve against the CURRENT criteria.
    // They can stop resolving out of band (a `propose_card` that rewrites the
    // acceptance-criteria green carries the partition forward), and the remap
    // at the end silently DROPS anything it cannot resolve — so without this a
    // stale partition ships with criteria quietly deleted from it, past both
    // postconditions, and the caller re-stamps the fingerprint that would have
    // caught it at apply time. Refusing sends the caller back to a re-propose,
    // which is the only honest answer: a partition of criteria that no longer
    // exist is not a partition.
    if (c.coveredCriteria.some((i) => i >= params.criteria.length)) {
      return err("criteria_moved");
    }
    working.push({
      childId: c.childId,
      base: c,
      title: c.title,
      description: c.description,
      covered: new Set(c.coveredCriteria),
      touched: false,
      approach: c.approach,
      approachSig: c.scopeSig,
    });
  }
  const byId = new Map(working.map((w) => [w.childId, w]));
  const find = (id: string): Working | undefined => byId.get(id);

  const parkedIndices = new Set<number>();
  const unparking: ParkedCriterion[] = [];
  const parkedByText = new Map(params.parked.map((p) => [p.text, p]));
  let seq = params.nextChildSeq;

  /** Resolve an "AC<n>" against the PRE-op criteria list. */
  const resolve = (raw: string): number | null =>
    criterionIdToIndex(raw, criteria.length);

  /** The scope signature of a set of PRE-op indices — the stamp an op that
   *  authors a build note leaves, for the output postcondition to compare
   *  against. Same shape as the one the output computes over the FINAL texts,
   *  so an index that survives renumbering compares equal and a criterion that
   *  moves, parks or un-parks does not. */
  const sigOfIndices = (indices: Iterable<number>): string =>
    computeScopeSig(
      [...indices].map((i) => criteria[i] ?? "").filter((t) => t !== ""),
    );

  for (const op of params.ops) {
    if (isModelRouted(op)) return err(`model_routed_op: ${op.op}`);

    switch (op.op) {
      case "rename": {
        const w = find(op.childId);
        if (!w) return err(`unknown_child_id: ${op.childId}`);
        w.title = op.title;
        w.touched = true;
        break;
      }
      case "edit_scope": {
        const w = find(op.childId);
        if (!w) return err(`unknown_child_id: ${op.childId}`);
        w.description = op.description;
        // The covered set does not move here, so the output's signature check
        // cannot see this: a re-worded scope line keeping the old build note is
        // invisible to `scopeSig` and is exactly the stale-guidance failure.
        // An omitted `approach` therefore CLEARS.
        w.approach = op.approach;
        w.approachSig = sigOfIndices(w.covered);
        w.touched = true;
        break;
      }
      case "merge": {
        if (op.fromChildId === op.intoChildId) return err("merge_into_self");
        const from = find(op.fromChildId);
        const into = find(op.intoChildId);
        if (!from) return err(`unknown_child_id: ${op.fromChildId}`);
        if (!into) return err(`unknown_child_id: ${op.intoChildId}`);
        for (const i of from.covered) into.covered.add(i);
        into.touched = true;
        byId.delete(from.childId);
        working.splice(working.indexOf(from), 1);
        break;
      }
      case "move_criteria": {
        const to = find(op.toChildId);
        if (!to) return err(`unknown_child_id: ${op.toChildId}`);
        for (const raw of op.criterionIds) {
          const index = resolve(raw);
          if (index === null) {
            return err(
              `invalid_criteria_id: ${raw} — valid ids are ` +
                criterionIdsFor(criteria.length).join(","),
            );
          }
          for (const w of working) {
            if (w.childId !== to.childId && w.covered.delete(index)) w.touched = true;
          }
          to.covered.add(index);
        }
        to.touched = true;
        break;
      }
      case "add_child": {
        const indices: number[] = [];
        for (const raw of op.criterionIds) {
          const index = resolve(raw);
          if (index === null) {
            return err(
              `invalid_criteria_id: ${raw} — valid ids are ` +
                criterionIdsFor(criteria.length).join(","),
            );
          }
          indices.push(index);
        }
        const childId = params.mintChildId(seq);
        seq += 1;
        if (byId.has(childId)) return err("child_id_collision");
        const fresh: Working = {
          childId,
          // `base` is a placeholder: estimate/files/localId are stamped by the
          // caller, which is the only layer that holds the sizing map.
          base: {
            localId: "",
            childId,
            title: op.title,
            description: op.description,
            coveredCriteria: [],
            estimate: { filesTouched: 0, locBucket: "XS" },
            files: [],
          },
          title: op.title,
          description: op.description,
          covered: new Set(indices),
          touched: true,
          approach: op.approach,
          approachSig: sigOfIndices(indices),
        };
        working.push(fresh);
        byId.set(childId, fresh);
        // A criterion a new piece claims leaves whoever else held it — otherwise
        // "add a piece for AC4" silently produces an overlap nobody asked for.
        for (const w of working) {
          if (w.childId === childId) continue;
          for (const i of indices) if (w.covered.delete(i)) w.touched = true;
        }
        break;
      }
      case "remove_rehome":
      case "remove": {
        const target = find(op.childId);
        if (!target) return err(`unknown_child_id: ${op.childId}`);
        // Every criterion this child covers that NO OTHER child covers needs a
        // disposition. A shared one needs none — it survives on its other owner.
        const orphaned = [...target.covered].filter(
          (i) => !working.some((w) => w.childId !== target.childId && w.covered.has(i)),
        );
        const decided = new Map<number, { kind: "child"; childId: string } | { kind: "park" }>();
        if (op.op === "remove_rehome") {
          for (const r of op.rehome) {
            const index = resolve(r.criterionId);
            if (index === null) {
              return err(
                `invalid_criteria_id: ${r.criterionId} — valid ids are ` +
                  criterionIdsFor(criteria.length).join(","),
              );
            }
            decided.set(index, { kind: "child", childId: r.toChildId });
          }
        } else {
          for (const d of op.disposition) {
            const index = resolve(d.criterionId);
            if (index === null) {
              return err(
                `invalid_criteria_id: ${d.criterionId} — valid ids are ` +
                  criterionIdsFor(criteria.length).join(","),
              );
            }
            decided.set(index, d.to);
          }
        }
        const undecided = orphaned.filter((i) => !decided.has(i));
        if (undecided.length > 0) {
          return err(
            "orphan_criteria: " +
              undecided.map((i) => namedCriterion(criteria, i)).join("; ") +
              " — every criterion this piece uniquely covers needs a destination:" +
              " name a surviving piece for each" +
              (op.op === "remove_rehome"
                ? ". You cannot drop one — if the user means the work should not" +
                  " be built at all, say so and let them decide."
                : ", or park it out of scope."),
          );
        }
        byId.delete(target.childId);
        working.splice(working.indexOf(target), 1);
        for (const [index, to] of decided) {
          // The WORKING set, not the frozen `base`. The orphan computation above
          // reads `target.covered`, so validating the disposition against
          // `base.coveredCriteria` made the two halves of the same op disagree
          // the moment an earlier op in the list moved a criterion onto this
          // piece: naming it denied `criterion_not_on_child`, and omitting it
          // denied `orphan_criteria` — an unsatisfiable pair, and a repair loop
          // for a model that reads the deny and does exactly what it says.
          // Identical for a single op (the client's only shape), so this only
          // ever mattered on the model's multi-op revision lane.
          if (!target.covered.has(index)) {
            return err(
              `criterion_not_on_child: AC${String(index + 1)} is not covered by ` +
                op.childId,
            );
          }
          if (to.kind === "park") {
            if (!params.criteriaEditable) return err("parent_already_applied");
            // Parking a SHARED criterion strips it from its other owners too —
            // it is leaving the ticket, not moving. That can empty a sibling,
            // which is caught by the `empty_child` sweep below rather than
            // silently shipping a piece with no acceptance criteria.
            for (const w of working) if (w.covered.delete(index)) w.touched = true;
            parkedIndices.add(index);
          } else {
            const dest = find(to.childId);
            if (!dest) return err(`unknown_child_id: ${to.childId}`);
            dest.covered.add(index);
            dest.touched = true;
          }
        }
        break;
      }
      case "park_criteria": {
        if (!params.criteriaEditable) return err("parent_already_applied");
        for (const raw of op.criterionIds) {
          const index = resolve(raw);
          if (index === null) {
            return err(
              `invalid_criteria_id: ${raw} — valid ids are ` +
                criterionIdsFor(criteria.length).join(","),
            );
          }
          for (const w of working) if (w.covered.delete(index)) w.touched = true;
          parkedIndices.add(index);
        }
        break;
      }
      case "unpark_criteria": {
        if (!params.criteriaEditable) return err("parent_already_applied");
        const dest = find(op.toChildId);
        if (!dest) return err(`unknown_child_id: ${op.toChildId}`);
        for (const text of op.parkedTexts) {
          const row = parkedByText.get(text);
          if (!row) return err("unknown_parked_criterion");
          // Skipping a duplicate here restored ONE row while the caller's
          // `parked` filter removed EVERY parked entry sharing that text, so a
          // criterion parked twice (parked, restored, parked again, or two
          // identical rows) lost a record with nothing to show for it. Refuse
          // instead: the user cannot mean anything unambiguous by it.
          if (unparking.some((u) => u.text === text)) {
            return err("ambiguous_parked_criterion");
          }
          unparking.push(row);
        }
        dest.touched = true;
        break;
      }
    }
  }

  // ---- postconditions over the RESULT, not over the ops ----

  if (working.length < MIN_CHILDREN) {
    return err(
      `child_count_out_of_bounds: got=${String(working.length)} min=${String(MIN_CHILDREN)}` +
        ` max=${String(maxChildren)}`,
    );
  }
  if (working.length > maxChildren) {
    return err(
      `child_count_out_of_bounds: got=${String(working.length)} min=${String(MIN_CHILDREN)}` +
        ` max=${String(maxChildren)}`,
    );
  }

  const empties = working.filter((w) => w.covered.size === 0);
  if (empties.length > 0) {
    // Reachable when a criterion the user parked was the LAST one on a sibling
    // — the dialog inspects the piece being removed, not the blast radius.
    // Naming the stranded pieces is what lets the caller re-ask rather than ship
    // a child whose acceptance-criteria section would be empty.
    return err(
      "empty_child: " +
        empties.map((w) => `"${w.title}"`).join("; ") +
        " — every piece must still deliver at least one acceptance criterion." +
        " Remove or merge the piece too, or leave that criterion in scope.",
    );
  }

  const survivingIndices = new Set(criteria.map((_, i) => i));
  for (const i of parkedIndices) survivingIndices.delete(i);
  const uncovered = [...survivingIndices].filter(
    (i) => !working.some((w) => w.covered.has(i)),
  );
  if (uncovered.length > 0) {
    return err(
      "coverage_gap: " +
        uncovered.map((i) => namedCriterion(criteria, i)).join("; ") +
        " — every criterion still in scope must be covered by at least one piece.",
    );
  }

  // ---- ONE index remap, after all parking is settled ----
  //
  // An un-parked row re-enters where it left from, so a park followed by an
  // un-park of the same criterion reproduces the pre-park array byte for byte.
  // That is what makes "changed my mind" genuinely net-zero: the rendered value
  // matches, so `criteriaFingerprintOf` matches, so the confirmed implementation
  // plan survives and the caller skips a DoR re-judge whose degrade path would
  // clear `allGreen` on a card the user had already finished.
  //
  // `parkedAtIndex` is a SPLICE POSITION into the survivors, not a sort key over
  // original indices — and the difference is not cosmetic. It was recorded
  // against the list as it stood WITH the row present, i.e. the list the
  // survivors form once the row is taken out is exactly the list it has to be
  // re-inserted into. Treating it as a sort key compares two different coordinate
  // systems the moment any OTHER row has been parked since, and lands the
  // restored criterion one place late.
  //
  // Ascending order so an earlier insertion is already in place when a later one
  // is measured, and a row with no recorded position (parked before the field
  // existed) appends — today's behaviour, and the only honest answer for a
  // position nobody wrote down.
  const finalTexts = criteria.filter((_, i) => !parkedIndices.has(i));
  const ordered = [...unparking].sort(
    (a, b) =>
      (a.parkedAtIndex ?? Number.MAX_SAFE_INTEGER) -
      (b.parkedAtIndex ?? Number.MAX_SAFE_INTEGER),
  );
  const unparkedAtByText = new Map<string, number>();
  for (const u of ordered) {
    const at = Math.min(u.parkedAtIndex ?? finalTexts.length, finalTexts.length);
    finalTexts.splice(at, 0, u.text);
    // Every row already placed at or after this position just moved right.
    for (const [text, pos] of unparkedAtByText) {
      if (pos >= at) unparkedAtByText.set(text, pos + 1);
    }
    unparkedAtByText.set(u.text, at);
  }
  // Old index → new index, read off the array that will actually be written
  // rather than derived a second way. `finalTexts` holds both survivors and
  // restored rows, so the survivor scan skips the positions the restores took.
  const restoredPositions = new Set(unparkedAtByText.values());
  const remap = new Map<number, number>();
  {
    let at = 0;
    for (let i = 0; i < criteria.length; i += 1) {
      if (parkedIndices.has(i)) continue;
      while (restoredPositions.has(at)) at += 1;
      remap.set(i, at);
      at += 1;
    }
  }
  // Where each un-parked row landed. Keyed by TEXT because that is the identity
  // the unpark op names, and `unparking` already refuses a duplicate
  // (`ambiguous_parked_criterion`), so the key is unique among restored rows.
  const unparkedAt = unparkedAtByText;

  const out: BreakdownChild[] = working.map((w) => {
    const remapped = [...w.covered]
      .map((i) => remap.get(i))
      .filter((i): i is number => i !== undefined)
      .sort((a, b) => a - b);
    // Un-parked rows land on the child the unpark named.
    const gained: number[] = [];
    if (unparking.length > 0) {
      for (const op of params.ops) {
        if (op.op === "unpark_criteria" && op.toChildId === w.childId) {
          for (const text of op.parkedTexts) {
            const at = unparkedAt.get(text);
            if (at !== undefined) gained.push(at);
          }
        }
      }
    }
    const covered = [...new Set([...remapped, ...gained])].sort((a, b) => a - b);
    const scopeSig = computeScopeSig(
      covered.map((i) => finalTexts[i] ?? "").filter((t) => t !== ""),
    );
    if (!w.touched && scopeSig === w.base.scopeSig) {
      // Nothing about this piece moved — carry the persisted row forward with
      // only its (renumbered) index array replaced. Its bytes are untouched by
      // construction, not by promise.
      return { ...w.base, coveredCriteria: covered };
    }
    // `approach` describes a SCOPE. It survives only when the piece's covered
    // criterion TEXTS are what the note was authored against — a postcondition
    // over the recomputed signature rather than N per-handler clears, so the
    // next op author cannot forget one. The spread below is `baseRest`, NOT
    // `w.base`: spreading the persisted row would carry a stale note through
    // every op silently, which is precisely the shape this branch already had
    // and why the check has to be subtractive.
    const approach = scopeSig === w.approachSig ? w.approach : undefined;
    const { approach: _dropped, approachRetired: _wasRetired, ...baseRest } = w.base;
    // "This piece HAD a note and it was dropped" — recorded, because at apply
    // time the absence of a note is otherwise indistinguishable from a model
    // that never wrote one, and the child body has to be able to say which.
    // Sticky across later ops while the piece still has no note (a second op on
    // the same piece must not launder the retirement away), and cleared the
    // moment one lands.
    const hadNote =
      (w.approach ?? "").trim() !== "" || w.base.approachRetired === true;
    const retired = approach === undefined && hadNote;
    return {
      ...baseRest,
      childId: w.childId,
      title: w.title,
      description: w.description,
      coveredCriteria: covered,
      scopeSig,
      ...(approach !== undefined ? { approach } : {}),
      ...(retired ? { approachRetired: true } : {}),
    };
  });

  // The `empty_child` sweep above runs on the PRE-remap sets, which is the right
  // place to catch a park that strands a sibling — but it cannot see a row the
  // remap drops. Re-assert it over what will actually be written, so the
  // invariant it states ("every piece must still deliver at least one
  // acceptance criterion") holds over the persisted array rather than over an
  // intermediate one. Unreachable now that out-of-range indices are refused up
  // front; kept because it is the postcondition, not the precondition.
  const emptyAfter = out.filter((c) => c.coveredCriteria.length === 0);
  if (emptyAfter.length > 0) {
    return err(
      "empty_child: " +
        emptyAfter.map((c) => `"${c.title}"`).join("; ") +
        " — every piece must still deliver at least one acceptance criterion.",
    );
  }

  return {
    ok: true,
    children: out,
    touched: new Set(working.filter((w) => w.touched).map((w) => w.childId)),
    parking: [...parkedIndices]
      .sort((a, b) => a - b)
      .map((index) => ({ index, text: criteria[index] ?? "" })),
    unparking,
    criteria: finalTexts,
    nextChildSeq: seq,
  };
};

// ---------------------------------------------------------------------------
// The executable form of "the other pieces are unchanged"
// ---------------------------------------------------------------------------

export class AssignmentPlaneCorrupt extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AssignmentPlaneCorrupt";
  }
}

/**
 * Assert that no piece outside `touched` changed what it SAYS or what it
 * DELIVERS.
 *
 * Key-based, not reference-based, and that distinction is load-bearing.
 * `coveredCriteria` is an array of positional indices into the current criteria
 * split, so parking one criterion renumbers the indices of every piece after it
 * — an untouched piece MUST become a new object. A reference-identity
 * postcondition (`expect(after[i]).toBe(before[j])`) would therefore throw on
 * the single most important flow this whole design exists to support. The
 * honest invariant is that the piece's title, its scope line, and the criterion
 * TEXTS it delivers are identical; which integers encode those texts is an
 * implementation detail of the storage layer.
 *
 * Throws rather than denying: a violation is a server bug, not something a user
 * or a model can repair by trying again.
 */
export const assertUnchangedExcept = (
  before: ReadonlyArray<BreakdownChild>,
  after: ReadonlyArray<BreakdownChild>,
  touched: ReadonlySet<string>,
  beforeCriteria: ReadonlyArray<string>,
  afterCriteria: ReadonlyArray<string>,
): void => {
  // NOT `.filter(undefined)`. Dropping an unresolvable index on both sides made
  // the comparison vacuous exactly when it mattered: a piece whose indices had
  // gone stale compared "" to "" and passed, so the assertion certified as
  // unchanged the one shape it exists to catch. A hole is now a distinct,
  // non-matching token, so it fails loudly instead.
  const textsOf = (
    child: BreakdownChild,
    criteria: ReadonlyArray<string>,
  ): string =>
    computeScopeSig(
      child.coveredCriteria.map((i) => criteria[i] ?? `\u0000unresolved:${String(i)}`),
    );

  const afterById = new Map(
    after.filter((c) => c.childId !== undefined).map((c) => [c.childId, c]),
  );
  for (const prev of before) {
    if (prev.childId === undefined) continue;
    if (touched.has(prev.childId)) continue;
    const now = afterById.get(prev.childId);
    // Absent is legal: a removal or a merge takes a piece out, and both name
    // the piece they consume. `touched` covers renames/edits, not disappearance.
    if (!now) continue;
    if (now.title !== prev.title) {
      throw new AssignmentPlaneCorrupt(
        `untouched child ${prev.childId} changed title`,
      );
    }
    if (now.description !== prev.description) {
      throw new AssignmentPlaneCorrupt(
        `untouched child ${prev.childId} changed description`,
      );
    }
    if (textsOf(now, afterCriteria) !== textsOf(prev, beforeCriteria)) {
      throw new AssignmentPlaneCorrupt(
        `untouched child ${prev.childId} changed covered criteria`,
      );
    }
    // The build note is model-authored text that gets WRITTEN to GitLab, so it
    // belongs under the same promise as the title and the scope line. `?? ""`
    // on both sides: a note appearing on a piece nobody named is as much a
    // violation as one changing, and an absent-vs-empty distinction here would
    // only make the assertion easier to slip past.
    if ((now.approach ?? "") !== (prev.approach ?? "")) {
      throw new AssignmentPlaneCorrupt(
        `untouched child ${prev.childId} changed approach`,
      );
    }
    // The retirement marker is written into the GitLab body as a sentence ("Loom
    // had a note here and dropped it"), so it is under the same promise: a piece
    // nobody named must not start explaining an absence that never happened.
    if ((now.approachRetired ?? false) !== (prev.approachRetired ?? false)) {
      throw new AssignmentPlaneCorrupt(
        `untouched child ${prev.childId} changed approachRetired`,
      );
    }
  }
};

/**
 * Backfill `childId` / `nextChildSeq` onto a partition persisted before typed
 * ops existed, and re-derive `scopeSig` for any row missing one.
 *
 * Returns null when nothing needed doing, so a caller on a READ path can skip
 * the write. The read path must call this: `reemitPersistedSizingAndBreakdown`
 * runs on every bridge attach, and a proposal persisted before the deploy would
 * otherwise re-emit to a new client with no addressable ids at all — the typed
 * buttons would render and every op would deny `unknown_child_id`.
 */
export const ensureChildIds = (params: {
  readonly children: ReadonlyArray<BreakdownChild>;
  readonly criteria: ReadonlyArray<string>;
  readonly nextChildSeq: number | undefined;
  readonly mintChildId: (seq: number) => string;
}): {
  readonly children: ReadonlyArray<BreakdownChild>;
  readonly nextChildSeq: number;
} | null => {
  let seq = params.nextChildSeq ?? 0;
  let changed = false;
  const taken = new Set(
    params.children
      .map((c) => c.childId)
      .filter((id): id is string => id !== undefined),
  );
  const children = params.children.map((c) => {
    const scopeSig =
      c.scopeSig ??
      computeScopeSig(
        c.coveredCriteria
          .map((i) => params.criteria[i])
          .filter((t): t is string => t !== undefined),
      );
    if (c.childId !== undefined) {
      if (c.scopeSig !== undefined) return c;
      changed = true;
      return { ...c, scopeSig };
    }
    let childId = params.mintChildId(seq);
    seq += 1;
    // Deterministic minting can only collide if a seq is reused; loop rather
    // than trust the counter, since it is itself back-filled here.
    while (taken.has(childId)) {
      childId = params.mintChildId(seq);
      seq += 1;
    }
    taken.add(childId);
    changed = true;
    return { ...c, childId, scopeSig };
  });
  if (!changed && params.nextChildSeq !== undefined) return null;
  return { children, nextChildSeq: seq };
};

/** Guard for the one namespace the model must not mint into. */
export const rejectsServerLocalId = (localId: string): boolean =>
  isServerLocalId(localId);
